import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { toolboxTalksService } from '../../services/toolboxTalksService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import TalkCreatePanel from './components/TalkCreatePanel';
import TalkAssignPanel from './components/TalkAssignPanel';
import AttendanceTrackingTable from './components/AttendanceTrackingTable';
import ToolboxDeleteConfirmModal from './components/ToolboxDeleteConfirmModal';
import { pdfExportService } from '../../services/pdfExportService';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';

const ToolboxTalksManagement = () => {
  const { user, userProfile } = useAuth();
  // NSherjFh PR 10: Delete + Restore + the View Deleted toggle are
  // admin-only — same gating decision as PRs 3, 8, 9. RLS would
  // permit site_managers via toolbox_talks_admin_sm_update, but the
  // umbrella standard is admin-only UI for destructive actions. SMs
  // continue to create + assign + monitor confirmations.
  const isAdmin = userProfile?.role === 'admin';
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [company, setCompany] = useState(null);
  const [talks, setTalks] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [siteWorkers, setSiteWorkers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [exportingPdfId, setExportingPdfId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('');

  // NSherjFh PR 10 — recoverable soft-delete state. Build-from-
  // scratch (no existing delete UI). Mirrors PR 9 inductions
  // structure exactly.
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingTalk, setDeletingTalk] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDeleted]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [companyResult, sitesResult] = await Promise.all([
        companyService?.getUserCompany(),
        siteService?.getSitesByUser(),
      ]);

      const comp = companyResult?.data;
      const siteList = sitesResult?.data || [];
      setCompany(comp);
      setSites(siteList);

      if (comp?.id) {
        await loadTalksData(comp?.id);
      }
    } catch (err) {
      setErrorMsg('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatErr = (err, fallback) => {
    if (err?.code === 'PGRST301' || err?.code === '42501') return `Permission denied: ${err?.message}`;
    return `${fallback}: ${err?.message || 'Unknown error'}`;
  };

  const loadTalksData = async (companyId) => {
    // NSherjFh PR 10: in viewDeleted mode, fetch talks with the
    // service-level `{ includeDeleted: true }` opt-in then post-filter
    // to deleted-only on the page (the service can't easily express
    // "only deleted" — it's either active-only or all). Assignment
    // tracking is skipped in deleted view because the active grid
    // covers only the active set. Mirrors PR 9 inductions.
    const [talksResult, assignmentsResult] = await Promise.all([
      toolboxTalksService?.getTalksForCompany(companyId, { includeDeleted: viewDeleted }),
      viewDeleted
        ? Promise.resolve({ data: [], error: null })
        : toolboxTalksService?.getAllAssignmentsForCompany(companyId),
    ]);
    if (talksResult?.error) {
      setErrorMsg(formatErr(talksResult?.error, 'Failed to load toolbox talks'));
    } else if (assignmentsResult?.error) {
      setErrorMsg(formatErr(assignmentsResult?.error, 'Failed to load talk assignments'));
    }
    const rows = talksResult?.data || [];
    setTalks(viewDeleted ? rows?.filter((t) => t?.deleted_at) : rows);
    setAllAssignments(assignmentsResult?.data || []);
  };

  const handleSiteChangeForWorkers = async (siteId) => {
    if (!siteId) { setSiteWorkers([]); return; }
    const result = await toolboxTalksService?.getWorkersForSite(siteId);
    if (result?.error) {
      toast?.error(formatErr(result?.error, 'Failed to load workers for this site'));
    }
    setSiteWorkers(result?.data || []);
  };

  const handleCreateTalk = async ({ title, siteId, file }) => {
    if (subscriptionReadOnly) return;
    if (!company?.id || !user?.id) return;
    if (siteId) {
      const { data: pickedSite } = await supabase
        ?.from('sites')
        ?.select('status')
        ?.eq('id', siteId)
        ?.maybeSingle();
      if (isSiteReadOnlyStatus(pickedSite?.status)) {
        toast?.error('This site is completed — please reactivate to continue.');
        return;
      }
    }
    setCreating(true);
    setErrorMsg('');
    const result = await toolboxTalksService?.createTalk(title, siteId, file, user?.id, company?.id);
    setCreating(false);
    if (result?.error) {
      setErrorMsg(formatErr(result?.error, 'Failed to create toolbox talk'));
      return;
    }
    setSuccessMsg('Toolbox talk created successfully');
    setTimeout(() => setSuccessMsg(''), 4000);
    await loadTalksData(company?.id);
    await handleSiteChangeForWorkers(siteId);
  };

  const handleAssign = async (talkId, workerIds) => {
    if (subscriptionReadOnly) return;
    setAssigning(true);
    setErrorMsg('');
    const result = await toolboxTalksService?.assignTalkToWorkers(talkId, workerIds);
    setAssigning(false);
    if (result?.error) {
      setErrorMsg(formatErr(result?.error, 'Assignment failed'));
      return;
    }
    setSuccessMsg(`Talk assigned to ${workerIds?.length} worker${workerIds?.length !== 1 ? 's' : ''}`);
    setTimeout(() => setSuccessMsg(''), 4000);
    if (company?.id) await loadTalksData(company?.id);
  };

  // NSherjFh PR 10 — soft-delete handlers. Mirrors PR 9 inductions.
  const handleDeleteClick = (talk) => {
    setDeletingTalk(talk);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTalk?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingTalk;
    try {
      const result = await toolboxTalksService?.softDeleteToolboxTalk(target?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Delete failed');
      setTalks((prev) => prev?.filter((t) => t?.id !== target?.id));
      toast?.success(`"${target?.title}" moved to Deleted`);
      // Refresh assignments so stats reflect the change.
      if (company?.id) await loadTalksData(company?.id);
    } catch (err) {
      console.error('[ToolboxTalksManagement] delete failed:', err);
      toast?.error(err?.message || 'Failed to delete toolbox talk');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingTalk(null);
    }
  };

  const handleRestore = async (talk) => {
    try {
      const result = await toolboxTalksService?.restoreToolboxTalk(talk?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Restore failed');
      setTalks((prev) => prev?.filter((t) => t?.id !== talk?.id));
      toast?.success(`"${talk?.title}" restored`);
    } catch (err) {
      console.error('[ToolboxTalksManagement] restore failed:', err);
      toast?.error(err?.message || 'Failed to restore toolbox talk');
    }
  };

  const handleExportTalkPdf = async (talk) => {
    try {
      setExportingPdfId(talk?.id);
      const talkAssignments = allAssignments?.filter(a => a?.talk_id === talk?.id || a?.toolbox_talk_id === talk?.id);
      const site = sites?.find(s => s?.id === talk?.site_id);
      await pdfExportService?.generateToolboxTalkAttendance({
        talk,
        assignments: talkAssignments,
        confirmations: talkAssignments?.filter(a => a?.confirmed),
        siteName: site?.site_name || '',
        companyName: company?.company_name || '',
      });
    } catch (err) {
      console.error('PDF export error:', err);
      toast?.error('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdfId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Toolbox Talks Management - SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-6">

            <SubscriptionReadOnlyBanner className="mb-6" />

            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Icon name="MessageSquare" size={20} className="text-accent" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {viewDeleted ? 'Deleted toolbox talks' : t('toolboxTitle')}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {viewDeleted
                      ? 'Toolbox talks removed from active views. Restore to bring them back.'
                      : t('toolboxSubtitle')}
                  </p>
                </div>
              </div>
              {/* NSherjFh PR 10: View Deleted toggle is admin-only. */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  onClick={() => { setErrorMsg(''); setSuccessMsg(''); setViewDeleted((v) => !v); }}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  iconPosition="left"
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
            </div>

            {/* Alerts */}
            {successMsg && (
              <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="CheckCircle" size={16} />
                <span>{successMsg}</span>
              </div>
            )}
            {errorMsg && (
              <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* NSherjFh PR 10: active view = full management workspace;
                deleted view = just a restorable list. Hide stats /
                Create / Assign / tracking grid when viewing the
                recycle bin. */}
            {!viewDeleted && (
              <>
                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: t('toolboxTotalTalks'), value: talks?.length, icon: 'MessageSquare', color: 'text-accent bg-accent/10' },
                    { label: t('toolboxAssigned'), value: allAssignments?.length, icon: 'Users', color: 'text-blue-600 bg-blue-50' },
                    { label: t('toolboxConfirmed'), value: allAssignments?.filter((a) => a?.confirmed)?.length, icon: 'CheckCircle', color: 'text-success bg-success/10' },
                    { label: t('toolboxPending'), value: allAssignments?.filter((a) => !a?.confirmed)?.length, icon: 'Clock', color: 'text-destructive bg-destructive/10' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat?.color}`}>
                          <Icon name={stat?.icon} size={18} />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                          <p className="text-xs text-muted-foreground">{stat?.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Create + Assign Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <TalkCreatePanel
                    sites={sites}
                    onCreateTalk={handleCreateTalk}
                    creating={creating}
                    onSiteChange={handleSiteChangeForWorkers}
                    readOnly={subscriptionReadOnly}
                  />
                  <TalkAssignPanel
                    talks={talks}
                    workers={siteWorkers}
                    onAssign={handleAssign}
                    onTalkChange={handleSiteChangeForWorkers}
                    assigning={assigning}
                    readOnly={subscriptionReadOnly}
                  />
                </div>

                {/* Attendance Tracking Table */}
                <AttendanceTrackingTable
                  rows={allAssignments}
                  loading={false}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  filterStatus={filterStatus}
                  onFilterStatusChange={setFilterStatus}
                  filterSite={filterSite}
                  onFilterSiteChange={setFilterSite}
                  sites={sites}
                />

                {/* Per-talk list — Export PDF + admin Delete buttons.
                    NSherjFh PR 10: extends the existing PDF-export
                    list with a Delete affordance on the same row so
                    admins don't have to hunt across separate sections. */}
                {talks?.length > 0 && (
                  <div className="mt-6 bg-card border border-border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="MessageSquare" size={16} className="text-accent" />
                      {t('toolboxExportPdfSection')}
                    </h3>
                    <div className="space-y-2">
                      {talks?.map(talk => (
                        <div key={talk?.id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
                          <span className="text-sm text-foreground min-w-0 truncate">{talk?.title}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleExportTalkPdf(talk)}
                              disabled={exportingPdfId === talk?.id}
                              className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 disabled:opacity-50 px-3 py-1.5 rounded-lg border border-accent/30 hover:bg-accent/5 transition-colors"
                            >
                              <Icon name="Download" size={13} />
                              {exportingPdfId === talk?.id ? t('exporting') : t('exportPdf')}
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteClick(talk)}
                                className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:text-destructive/80 px-3 py-1.5 rounded-lg border border-destructive/30 hover:bg-destructive/5 transition-colors"
                                aria-label={`Delete ${talk?.title}`}
                              >
                                <Icon name="Trash2" size={13} />
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* NSherjFh PR 10: deleted-talks view — restorable list. */}
            {viewDeleted && (
              <div className="bg-card border border-border rounded-xl p-4">
                {talks?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No deleted toolbox talks.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {talks?.map((talk) => (
                      <div key={talk?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{talk?.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {talk?.site?.site_name || 'Site removed'}
                            {talk?.deleted_at ? ` · deleted ${new Date(talk?.deleted_at)?.toLocaleDateString('en-GB')}` : ''}
                          </p>
                        </div>
                        {isAdmin && (
                          <button
                            onClick={() => handleRestore(talk)}
                            className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 px-3 py-1.5 rounded-lg border border-accent/30 hover:bg-accent/5 transition-colors"
                            aria-label={`Restore ${talk?.title}`}
                          >
                            <Icon name="RotateCcw" size={13} />
                            Restore
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
        <ToolboxDeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setDeletingTalk(null); }}
          onConfirm={handleConfirmDelete}
          talkTitle={deletingTalk?.title || ''}
        />
      </div>
    </>
  );
};

export default ToolboxTalksManagement;
